import { FileSignature, Check, Quote } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { PageCTA } from "../components/PageCTA";
import { Reveal, Overline } from "../components/Reveal";
import { useLang } from "../context/LanguageContext";
import { EXTRA } from "../content/extra";

const HERO_IMG = "https://images.unsplash.com/photo-1641477176034-1a3e10c343a8";

export default function StandardPage() {
  const { t, lang } = useLang();
  const c = t.pages.code;
  const sc = EXTRA[lang].standardCert;

  return (
    <div data-testid="standard-page">
      <PageHero overline={sc.overline} title={sc.title} image={HERO_IMG} />

      <section className="relative z-10 py-20 md:py-28 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <p className="font-display text-2xl md:text-3xl leading-snug text-white/85">{sc.intro}</p>
          </Reveal>
        </div>
      </section>

      {/* Conditions for granting / holding the certificate */}
      <section className="relative z-10 py-20 md:py-28 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Overline>{sc.overline}</Overline>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight">{sc.conditionsTitle}</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {sc.conditions.map((cond, i) => (
              <Reveal key={i} delay={(i % 2) * 0.08}>
                <div className="group h-full border border-white/10 hover:border-[#D4AF37]/50 transition-colors duration-300 p-7 flex items-start gap-5">
                  <span className="w-9 h-9 shrink-0 flex items-center justify-center bg-[#D4AF37] text-black font-display text-lg">{i + 1}</span>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl mb-2">{cond.title}</h3>
                    <p className="text-white/55 leading-relaxed">{cond.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14 border-l-2 border-[#D4AF37] pl-6 md:pl-8 max-w-3xl">
              <Quote size={28} className="text-[#D4AF37]/60 mb-3" />
              <p className="font-display italic text-xl md:text-2xl text-white/85 leading-snug">{sc.quote}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 py-20 md:py-28 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <p className="font-display text-2xl md:text-3xl leading-snug text-white/85">{c.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="relative z-10 py-20 md:py-28 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Overline>{c.overline}</Overline>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight">{c.pillarsTitle}</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-px mt-14 bg-white/10">
            {c.pillars.map((p, i) => (
              <Reveal key={i} delay={(i % 2) * 0.08}>
                <div className="group h-full bg-[#0a0a0a] p-8 hover:bg-[#111] transition-colors duration-400">
                  <div className="flex items-start gap-5">
                    <span className="font-display text-4xl text-[#D4AF37]/60 group-hover:text-[#D4AF37] transition-colors leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-display text-xl md:text-2xl mb-2">{p.title}</h3>
                      <p className="text-white/55 leading-relaxed">{p.text}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 py-20 md:py-28 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <div className="border border-[#D4AF37]/30 p-8 md:p-12">
              <FileSignature size={28} className="text-[#D4AF37] mb-6" strokeWidth={1.5} />
              <h3 className="font-display text-2xl md:text-3xl mb-4">{c.declarationTitle}</h3>
              <p className="text-white/65 leading-relaxed italic">{c.declaration}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <PageCTA label={t.pages.common.contactCta} />
    </div>
  );
}
